// live.jsx — the page's single connection to the server's live state.
//
// One EventSource for the whole document, shared by every component that needs it. The stream
// carries two named event types: "messages" (the status log) and "presence" (online/away/offline).
// Components subscribe through useLive / usePresence instead of fetching, so presence cannot
// disagree with itself between the hero, the status card and the footer.
//
// The store is module state, so "one connection for the whole document" is now enforced by the
// module system rather than by script order — hero.jsx and sections.jsx both import it and both
// get the same instance.

import React from "react";

const LiveStore = (function () {
  // null means "no frame has arrived yet", which is different from "empty" and from "offline".
  let state = { messages: null, presence: null };
  const listeners = new Set();
  let started = false;

  function set(next) {
    state = next;
    listeners.forEach((fn) => fn(state));
  }

  function parse(event, key) {
    try {
      const value = JSON.parse(event.data);
      set(Object.assign({}, state, { [key]: value }));
    } catch (err) {
      // A truncated frame is not worth recovering from — the next one carries the whole state.
    }
  }

  function start() {
    if (started) return;
    started = true;

    if (typeof EventSource === "undefined") {
      // No push support: one shot each, then the page stays as it loaded.
      fetch("/api/status").then((r) => r.json())
        .then((m) => set(Object.assign({}, state, { messages: Array.isArray(m) ? m : [] })))
        .catch(() => {});
      fetch("/api/presence").then((r) => r.json())
        .then((p) => set(Object.assign({}, state, { presence: p })))
        .catch(() => {});
      return;
    }

    // EventSource reconnects by itself, and the server answers every new connection with the
    // full current state of both channels, so there is no catch-up logic here.
    const es = new EventSource("/api/status/stream");
    es.addEventListener("messages", (e) => parse(e, "messages"));
    es.addEventListener("presence", (e) => parse(e, "presence"));
  }

  return {
    get: () => state,
    subscribe(fn) {
      listeners.add(fn);
      start();
      fn(state);
      return () => listeners.delete(fn);
    }
  };
})();

function useLive() {
  const [snapshot, setSnapshot] = React.useState(LiveStore.get());
  React.useEffect(() => LiveStore.subscribe(setSnapshot), []);
  return snapshot;
}

// Before the first frame the state is genuinely unknown. Guessing "online" here would be a lie
// on exactly the pages where it matters, so callers get "unknown" and render a neutral dot.
const PRESENCE_UNKNOWN = { state: "unknown", message: "", source: "unknown" };

function usePresence() {
  return useLive().presence || PRESENCE_UNKNOWN;
}

// The plain dot-plus-text rendering, used wherever the layout has nothing else to say.
// Spots that need to interleave other content (the status card's clock) read usePresence directly.
function PresenceBadge({ className }) {
  const presence = usePresence();
  return (
    <span className={className}>
      <span className={"live-dot is-" + presence.state}></span>
      {" " + presence.state + (presence.message ? " · " + presence.message : "")}
    </span>
  );
}

export { LiveStore, PresenceBadge, useLive, usePresence };
